import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Ban } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Quick Block" dialog (Session 27).
 *
 * Lets a provider block a date range with a one-click reason: Vacation,
 * Fully booked, Emergency only, or custom. Greys out those days on the
 * client booking calendar instantly.
 */
const REASONS = [
  { value: 'Vacation',       label: 'Vacation / Day off' },
  { value: 'Fully booked',   label: 'Fully booked' },
  { value: 'Emergency only', label: 'Emergency only' },
  { value: 'Unavailable',    label: 'Other / Unavailable' },
];

export default function QuickBlockDialog({ onBlocked }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('Vacation');

  const submit = async (e) => {
    e.preventDefault();
    if (!fromDate) {
      toast.error('Pick a start date');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/quick-block`, {
        fromDate,
        toDate: toDate || fromDate,
        reason,
      }, { headers });
      toast.success(`Blocked ${res.data.blockedCount} day${res.data.blockedCount === 1 ? '' : 's'}`);
      setFromDate(''); setToDate(''); setReason('Vacation');
      setOpen(false);
      onBlocked?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not block dates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="quick-block-btn">
          <Ban className="h-4 w-4 mr-1" /> Block Dates
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="quick-block-dialog">
        <DialogHeader>
          <DialogTitle>Block Dates</DialogTitle>
          <DialogDescription>
            Mark days as unavailable on your client booking calendar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="block-from-input" />
            </div>
            <div>
              <Label>To <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="block-to-input" />
            </div>
          </div>

          <div>
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="block-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value} data-testid={`block-reason-${r.value.replace(/\s+/g,'-').toLowerCase()}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-block-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Block dates
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
